/**
 * UserAutoEnrollmentV2 — Auto-enrollment confirmation page (V2 redesign)
 *
 * Flow:
 * 1. Display user data for confirmation
 * 2. Accept → enrolls user and redirects to dashboard
 * 3. Reject → shows simulator modal, then confirmation modal, then exits
 */
import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter
} from './ui/dialog';
import {
	Loader2,
	CheckCircle,
	XCircle,
	AlertTriangle,
	User,
	Mail,
	Calendar,
	CreditCard
} from 'lucide-react';
import { I18nContext } from '../context/translations/TranslationsContext.context';
import { ArcaContext } from '../context/ArcaContext.context';
import { CurrentUserI } from '../api/users/interfaces/Users';
import getCurrentUser from '../api/users/getCurrentUser';
import collectNoWill from '../api/users/collectWillOfNo';
import collectAutoEnrollment from '../api/users/collectAutoEnrollment';
import getCompanyInfoEppConfig from '../api/companies/getCompanyInfoEppConfig';
import { formatArcaNumber } from '../utils/formatArcaNumbers';
import { EmployerContributionTypeEnum } from '../types/enums';
import PensionSimulatorV2 from './SimulatorsV2/PensionSimulatorV2';

const UserAutoEnrollmentV2: React.FC = () => {
	const navigate = useNavigate();
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { token, companyId } = useContext(ArcaContext);

	// State
	const [loading, setLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [userData, setUserData] = useState<CurrentUserI | null>(null);
	const [companyEppInfo, setCompanyEppInfo] = useState<any>(null);

	// Dialog states
	const [showResultDialog, setShowResultDialog] = useState(false);
	const [resultMessage, setResultMessage] = useState('');
	const [isError, setIsError] = useState(false);

	const [showSimulatorDialog, setShowSimulatorDialog] = useState(false);
	const [showConfirmCancelDialog, setShowConfirmCancelDialog] = useState(false);

	const autoEnrollmentToken = token;

	// Fetch data
	useEffect(() => {
		const fetchData = async () => {
			if (!autoEnrollmentToken || !companyId) {
				setLoading(false);
				setIsError(true);
				setResultMessage(translate('user.autoEnrollment.invalidToken'));
				setShowResultDialog(true);
				return;
			}

			try {
				const [userResponse, companyResponse] = await Promise.all([
					getCurrentUser(autoEnrollmentToken),
					getCompanyInfoEppConfig(autoEnrollmentToken, companyId)
				]);

				if (!userResponse.success) {
					setLoading(false);
					setIsError(true);
					setResultMessage(userResponse.message || translate('user.autoEnrollment.error'));
					setShowResultDialog(true);
				} else {
					setUserData(userResponse.data || null);
					setCompanyEppInfo(companyResponse.data);
					setLoading(false);
				}
			} catch (error) {
				setLoading(false);
				setIsError(true);
				setResultMessage(translate('user.autoEnrollment.error'));
				setShowResultDialog(true);
			}
		};

		fetchData();
	}, [autoEnrollmentToken, companyId, translate]);

	// Calculate employer contribution
	const getEmployerContributionAmount = () => {
		if (!companyEppInfo?.companyEppConfig) return 0;
		const { employerContributionType, employerContributionAmount } =
			companyEppInfo.companyEppConfig;

		if (employerContributionType === EmployerContributionTypeEnum.FIXED_AMOUNT) {
			return Number(employerContributionAmount) || 0;
		}
		return 0;
	};

	const monthlyCompanyContributionText = () => {
		if (!companyEppInfo?.companyEppConfig) {
			return translate('onboarding.employee.noCompanyContribution');
		}

		const { employerContributionType, employerContributionAmount } =
			companyEppInfo.companyEppConfig;

		if (employerContributionType === EmployerContributionTypeEnum.GROSS_SALARY_PERCENTAGE) {
			return `${employerContributionAmount}% ${translate('autoEnrollment.ofGrossSalary')}`;
		}

		const monthlyAmount = getEmployerContributionAmount();
		if (monthlyAmount === 0) {
			return translate('onboarding.employee.noCompanyContribution');
		}

		return `${formatArcaNumber(monthlyAmount)}€/${translate('autoEnrollment.month')}`;
	};

	// Translate gender
	const translateGender = (gender: string) => {
		switch (gender) {
			case 'M':
				return translate('user.autoEnrollment.femenino');
			case 'V':
				return translate('user.autoEnrollment.masculino');
			case 'H':
				return translate('user.autoEnrollment.otro');
			default:
				return gender;
		}
	};

	// Handlers
	const handleAccept = async () => {
		if (isSubmitting) return;

		setIsSubmitting(true);
		setResultMessage(translate('user.autoEnrollment.processing'));
		setIsError(false);
		setShowResultDialog(true);

		try {
			const response = await collectAutoEnrollment(autoEnrollmentToken);

			if (response.success) {
				setResultMessage(translate('user.autoEnrollment.success'));
				setIsError(false);
				setTimeout(() => navigate('/dashboard/home'), 2000);
			} else {
				setResultMessage(response.message || translate('user.autoEnrollment.error'));
				setIsError(true);
				setIsSubmitting(false);
			}
		} catch (error) {
			setResultMessage(translate('user.autoEnrollment.error'));
			setIsError(true);
			setIsSubmitting(false);
		}
	};

	const handleReject = () => {
		setShowSimulatorDialog(true);
	};

	const handleConfirmReject = () => {
		setShowSimulatorDialog(false);
		setShowConfirmCancelDialog(true);
	};

	const handleFinalCancel = async () => {
		if (isSubmitting) return;

		setIsSubmitting(true);
		setShowConfirmCancelDialog(false);
		setResultMessage(translate('user.autoEnrollment.processingCancel'));
		setIsError(false);
		setShowResultDialog(true);

		try {
			const response = await collectNoWill(autoEnrollmentToken);

			if (response.success) {
				setResultMessage(translate('user.autoEnrollment.cancelSuccess'));
				setIsError(false);
				setTimeout(() => navigate('/'), 2000);
			} else {
				setResultMessage(response.message || translate('user.autoEnrollment.cancelError'));
				setIsError(true);
				setIsSubmitting(false);
			}
		} catch (error) {
			setResultMessage(translate('user.autoEnrollment.cancelError'));
			setIsError(true);
			setIsSubmitting(false);
		}
	};

	// Loading state
	if (loading) {
		return (
			<div className="min-h-screen bg-linear-to-b from-background to-muted/30 flex items-center justify-center">
				<Loader2 className="w-12 h-12 text-primary animate-spin" />
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-linear-to-b from-background to-muted/30 flex items-center justify-center p-4">
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				className="w-full max-w-2xl"
			>
				<Card className="border-none shadow-xl">
					<CardContent className="p-6 md:p-8">
						{/* Logo */}
						<div className="flex justify-center mb-6">
							<img src="/imgs/LOGO_VERDE_2.svg" alt="ARCA" className="h-10 w-auto" />
						</div>

						{/* Welcome */}
						<h1 className="text-2xl md:text-3xl font-bold text-center text-foreground mb-2">
							{translate('onboarding.employee.welcome')}{' '}
							{userData?.user?.name || userData?.user?.email}
						</h1>

						<p className="text-center text-muted-foreground mb-2">
							{translate('onboarding.employee.intro')}{' '}
							<span className="font-semibold text-foreground">{companyEppInfo?.company?.name}</span>{' '}
							{translate('onboarding.employee.intro2')}
						</p>

						<h2 className="text-lg font-semibold text-primary text-center mb-1">
							{translate('user.autoEnrollment.title')}
						</h2>
						<p className="text-sm text-muted-foreground text-center mb-6">
							{translate('user.autoEnrollment.subtitle')}
						</p>

						{/* User Data Form */}
						{userData && (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
								<div>
									<Label className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1">
										<CreditCard className="w-3 h-3" />
										{translate('user.autoEnrollment.dni')}
									</Label>
									<Input value={userData.user.nif || ''} disabled className="bg-muted/50" />
								</div>
								<div>
									<Label className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1">
										<Mail className="w-3 h-3" />
										{translate('user.autoEnrollment.email')}
									</Label>
									<Input value={userData.user.email || ''} disabled className="bg-muted/50" />
								</div>
								<div>
									<Label className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1">
										<User className="w-3 h-3" />
										{translate('user.autoEnrollment.nombre')}
									</Label>
									<Input value={userData.user.name || ''} disabled className="bg-muted/50" />
								</div>
								<div>
									<Label className="text-xs text-muted-foreground mb-1 block">
										{translate('user.autoEnrollment.primerApellido')}
									</Label>
									<Input
										value={userData.user.firstSurname || ''}
										disabled
										className="bg-muted/50"
									/>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground mb-1 block">
										{translate('user.autoEnrollment.segundoApellido')}
									</Label>
									<Input value={userData.user.lastSurname || ''} disabled className="bg-muted/50" />
								</div>
								<div>
									<Label className="text-xs text-muted-foreground flex items-center gap-1.5 mb-1">
										<Calendar className="w-3 h-3" />
										{translate('user.autoEnrollment.fechaNacimiento')}
									</Label>
									<Input value={userData.user.birthdate || ''} disabled className="bg-muted/50" />
								</div>
								<div className="md:col-span-2 flex justify-center">
									<div className="w-full md:w-1/2">
										<Label className="text-xs text-muted-foreground mb-1 block text-center">
											{translate('user.autoEnrollment.genero')}
										</Label>
										<Input
											value={translateGender(userData.user.gender)}
											disabled
											className="bg-muted/50 text-center"
										/>
									</div>
								</div>
							</div>
						)}

						{/* Action Buttons */}
						<div className="flex flex-col sm:flex-row gap-3 justify-center">
							<Button
								variant="outline"
								onClick={handleReject}
								disabled={isSubmitting}
								className="rounded-full border-destructive text-destructive hover:bg-destructive/10"
							>
								{translate('user.autoEnrollment.cancel')}
							</Button>
							<Button
								onClick={handleAccept}
								disabled={isSubmitting}
								className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
							>
								{isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
								{translate('user.autoEnrollment.accept')}
							</Button>
						</div>
					</CardContent>
				</Card>
			</motion.div>

			{/* Result Dialog */}
			<Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<div className="flex items-center gap-3">
							{isError ? (
								<div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
									<XCircle className="w-5 h-5 text-destructive" />
								</div>
							) : (
								<div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
									<CheckCircle className="w-5 h-5 text-primary" />
								</div>
							)}
							<DialogTitle>
								{isError ? translate('common.error') : translate('common.success')}
							</DialogTitle>
						</div>
					</DialogHeader>
					<DialogDescription>{resultMessage}</DialogDescription>
					{isError && (
						<DialogFooter>
							<Button onClick={() => navigate('/')} className="rounded-full">
								{translate('general.genericModal.goTo')}
							</Button>
						</DialogFooter>
					)}
				</DialogContent>
			</Dialog>

			{/* Simulator Dialog */}
			<Dialog open={showSimulatorDialog} onOpenChange={setShowSimulatorDialog}>
				<DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>{translate('onboarding.simulator.title')}</DialogTitle>
						<DialogDescription>
							{translate('onboarding.simulator.description.part1')}{' '}
							<span className="font-semibold">
								{formatArcaNumber(getEmployerContributionAmount() * 12)}€
							</span>{' '}
							{translate('onboarding.simulator.description.part2')}{' '}
							<span className="font-semibold">
								{formatArcaNumber(getEmployerContributionAmount() * 12)}€
							</span>{' '}
							{translate('onboarding.simulator.description.part3')}
						</DialogDescription>
					</DialogHeader>

					<div className="my-4">
						<PensionSimulatorV2 currentAge={0} />
					</div>

					<div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4 mb-4">
						<div className="flex items-start gap-3">
							<AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
							<p className="text-sm text-muted-foreground">
								{translate('onboarding.employee.areYouSureMessage')}{' '}
								<span className="font-semibold">{monthlyCompanyContributionText()}</span>{' '}
								<span className="font-semibold">
									{translate('onboarding.employee.areYouSureMessage2')}
								</span>
							</p>
						</div>
					</div>

					<DialogFooter className="gap-2">
						<Button
							onClick={() => setShowSimulatorDialog(false)}
							className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
						>
							{translate('general.genericModal.closeThinkAgain')}
						</Button>
						<Button
							variant="outline"
							onClick={handleConfirmReject}
							className="rounded-full border-destructive text-destructive hover:bg-destructive/10"
						>
							{translate('general.genericModal.confirmCancelAdhesion')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Final Confirmation Dialog */}
			<Dialog open={showConfirmCancelDialog} onOpenChange={setShowConfirmCancelDialog}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
								<AlertTriangle className="w-5 h-5 text-destructive" />
							</div>
							<DialogTitle>{translate('onboarding.employee.areYouSure')}</DialogTitle>
						</div>
					</DialogHeader>
					<DialogDescription>
						<p className="mb-2">
							{translate('onboarding.employee.areYouSureMessage')}{' '}
							<span className="font-semibold">{monthlyCompanyContributionText()}</span>{' '}
							<span className="font-semibold">
								{translate('onboarding.employee.areYouSureMessage2')}
							</span>
						</p>
						<p>{translate('onboarding.employee.cancelMessage')}</p>
					</DialogDescription>
					<DialogFooter className="gap-2">
						<Button
							onClick={() => setShowConfirmCancelDialog(false)}
							className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
						>
							{translate('general.genericModal.closeThinkAgain')}
						</Button>
						<Button
							variant="outline"
							onClick={handleFinalCancel}
							disabled={isSubmitting}
							className="rounded-full border-destructive text-destructive hover:bg-destructive/10 gap-2"
						>
							{isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
							{translate('general.genericModal.confirmCancelAdhesion')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default UserAutoEnrollmentV2;
